import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, Col, Row } from "reactstrap"
import axios from "axios"
import { api } from "../../utils/variables"
import TopAppCardM from "../MetaInsight/TopAppCardM"
import "./trending.css"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import { useFilters } from "../../hooks/useFilters"
import { useUrlPlatform } from "../../hooks/useUrlPlatform"
import InsightsPageHeader from "../../components/MetaInsight/InsightsPageHeader"
import TopAppCardSkeleton from "./TopAppCardSkeleton"
import PlatformsApi from "../../services/platformsApi"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, applySEODate } from "../../utils/seoUtils"

export default function TopDiscountsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  // Only set when reached via a Weekly Insights "View All" click — landing here
  // directly (sidebar link, bookmark, etc.) leaves this unset, so no back button shows.
  const cameFromWeeklyInsights = location.state?.from === 'weekly-insights'
  const [apps, setApps] = useState([])
  const [platforms, setPlatforms] = useState([])
  // Lives in the `?platform=` URL query param (defaulting to Meta) rather than
  // plain state, so it survives a round trip to an app's detail page and back,
  // and so a direct link can pre-select a platform.
  const [selectedPlatform, setSelectedPlatform] = useUrlPlatform('meta')
  const [platformsLoading, setPlatformsLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [sortedItems, setSortedItems] = useState([])
  const [isSorted, setIsSorted] = useState(false)
  const [rankSort, setRankSort] = useState(null)
  const [countriesList, setCountriesList] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(true)
  const [country, setCountry] = useState(() => {
    try { return sessionStorage.getItem('topDiscount_selectedCountry') || 'US' } catch (e) { return 'US' }
  })
  const [language, setLanguage] = useState(() => {
    try { return sessionStorage.getItem('topDiscount_selectedLanguage') } catch (e) { return null }
  })
  const [lastUpdated, setLastUpdated] = useState(null)
  const [itemsToShow, setItemsToShow] = useState(8)
  const [showAll, setShowAll] = useState(false)
  const { filterSections } = useFilters()
  const [topDiscountFilterPanelOpen, setTopDiscountFilterPanelOpen] = useState(false)
  const [topDiscountAppliedFilters, setTopDiscountAppliedFilters] = useState({})

  // Country/language select rendering (flags, react-select styling) now lives in
  // InsightsPageHeader; this page only needs to re-fetch when the country/language
  // changes.
  const handleTopDiscountCountryChange = (code, langForCountry) => {
    setCountry(code)
    setLanguage(langForCountry)
    try {
      sessionStorage.setItem('topDiscount_selectedCountry', code)
      if (langForCountry) sessionStorage.setItem('topDiscount_selectedLanguage', langForCountry)
      else sessionStorage.removeItem('topDiscount_selectedLanguage')
    } catch (e) { /* ignore */ }
    fetchTopDiscount(code, langForCountry)
  }

  const handleTopDiscountLanguageChange = (val) => {
    setLanguage(val)
    try {
      if (val) sessionStorage.setItem('topDiscount_selectedLanguage', val)
      else sessionStorage.removeItem('topDiscount_selectedLanguage')
    } catch (e) { /* ignore */ }
    fetchTopDiscount(country, val)
  }

  const handlePlatformChange = (platformId) => {
    setSelectedPlatform(platformId)
    fetchTopDiscount(country, language, topDiscountAppliedFilters, platformId)
  }

  const fetchTopDiscount = async (c = country, l = language, filters = {}, platform = selectedPlatform) => {
    try {
      setLoading(true)
      const params = {}
      if (c) params.country = c
      if (l) params.language = l

      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        if (filters.sort_by) params.sort_by = filters.sort_by
        if (filters.sort) params.sort_by = params.sort_by || filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }

      const token = localStorage.getItem('authToken')
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.get(`${api}/tracker/stats/${platform}/top_discount`, { params, headers })
      const games = res.data.games || []
      setApps(games)
      // reapply sort if active
      if (rankSort) {
        const arr = Array.isArray(games) ? [...games] : []
        arr.sort((a, b) => {
          const pa = (a && (a.rank || a.position || a.ranking || (a.meta && a.meta.rank))) || Number.MAX_SAFE_INTEGER
          const pb = (b && (b.rank || b.position || b.ranking || (b.meta && b.meta.rank))) || Number.MAX_SAFE_INTEGER
          return rankSort === 'asc' ? (Number(pa) - Number(pb)) : (Number(pb) - Number(pa))
        })
        setSortedItems(arr)
        setIsSorted(true)
      }
      setLastUpdated(new Date())
      sessionStorage.setItem('topDiscountMeta', JSON.stringify(games))
    } catch (err) {
      console.error('Error fetching top discount:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const load = async () => {
      setCountriesLoading(true)
      setPlatformsLoading(true)
      try {
        const [countriesRes, fetchedPlatforms] = await Promise.all([
          axios.get(`${api}/countries/`),
          PlatformsApi.fetchPlatforms()
        ])
        const res = countriesRes  // keep rest of logic using `res`
        const list = res.data?.data || []
        setCountriesList(list)
        setCountryOptions(list.map(c => ({ value: c.code, label: c.name })))
        setPlatforms(fetchedPlatforms || PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
        const ce = list.find(x => x.code === (country || 'US'))
        const defaultLang = ce && Array.isArray(ce.locales) && ce.locales.length ? ce.locales[0] : null
        setLanguage(prev => prev || defaultLang)
      } catch (e) {
        console.error('Error loading countries:', e)
        setPlatforms(PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
      } finally {
        setCountriesLoading(false)
        const initCountry = country || (countriesList && countriesList.length ? countriesList[0].code : 'US')
        const ce = countriesList.find(x => x.code === initCountry)
        const initLang = language || (ce && Array.isArray(ce.locales) && ce.locales.length ? ce.locales[0] : null)
        setCountry(prev => prev || initCountry)
        setLanguage(prev => prev || initLang)

        // restore persisted filters (survive route changes)
        let savedFilters = null
        try {
          const raw = sessionStorage.getItem('topDiscountAppliedFilters')
          savedFilters = raw ? JSON.parse(raw) : null
        } catch (err) {
          savedFilters = null
        }
        setTopDiscountAppliedFilters(savedFilters || {})

        fetchTopDiscount(initCountry, initLang, savedFilters || {})
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!rankSort) { setIsSorted(false); setSortedItems([]); return }
    const arr = Array.isArray(apps) ? [...apps] : []
    arr.sort((a, b) => {
      const pa = parseRank(a)
      const pb = parseRank(b)
      return rankSort === 'asc' ? (pa - pb) : (pb - pa)
    })
    setSortedItems(arr)
    setIsSorted(true)
  }, [apps, rankSort])

  const handleTopDiscountFilterApply = (filters) => {
    setTopDiscountAppliedFilters(filters)
    try { sessionStorage.setItem('topDiscountAppliedFilters', JSON.stringify(filters || {})) } catch (e) { /* ignore */ }
    setLoading(true)
    fetchTopDiscount(country, language, filters)
    setTopDiscountFilterPanelOpen(false)
  }

  const handleRemoveTopDiscountFilter = (key) => {
    const newFilters = { ...(topDiscountAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setTopDiscountAppliedFilters(newFilters)
      try { sessionStorage.setItem('topDiscountAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
      setLoading(true)
      fetchTopDiscount(country, language, newFilters)
    }
  }

  const parseRank = (app) => {
    if (!app) return Number.MAX_SAFE_INTEGER
    const candidates = [app.rank, app.position, app.ranking, app.rank_position, app.rankValue]
    for (let c of candidates) {
      if (c !== undefined && c !== null && c !== '' && !isNaN(Number(c))) return Number(c)
    }
    if (app.meta && (app.meta.rank !== undefined)) return Number(app.meta.rank)
    return Number.MAX_SAFE_INTEGER
  }

  const toggleRankSort = () => {
    setRankSort(rankSort === 'asc' ? 'desc' : 'asc')
  }

  useEffect(() => {
    if (showAll) setItemsToShow(apps.length)
    else setItemsToShow(8)
  }, [showAll, apps.length])

  const displayApps = isSorted ? sortedItems.slice(0, itemsToShow) : apps.slice(0, itemsToShow)

  // Description/disclaimer reference whichever store is currently selected, rather
  // than always saying "Meta" regardless of the active platform tab.
  const platformLabel = PlatformsApi.getPlatformDisplayName(selectedPlatform, platforms)
  const platformIconUrl = PlatformsApi.getPlatformIcon(selectedPlatform, platforms)

  return (
    <React.Fragment>
      <SEO
        title={applySEODate(PAGE_SEO.topDiscounts.title)}
        description={PAGE_SEO.topDiscounts.description}
        keywords={PAGE_SEO.topDiscounts.keywords}
        canonical={generateCanonicalUrl('/top-discounts')}
        robots={PAGE_SEO.topDiscounts.robots}
      />
    <div className="container-fluid" style={{ maxWidth: '100vw', padding: 32 }}>
      <div className="category_track_innersec">
        <div className="row">
          <div className="col-sm-12">
            <InsightsPageHeader
              onBack={cameFromWeeklyInsights ? () => navigate('/weekly-insights') : undefined}
              title="Top Discounts"
              description={`Discover the top discounted apps across the ${platformLabel} Store for your chosen country and language.`}
              disclaimer={`Insights are based on ${platformLabel} Store rankings and KeyQuest's tracking metrics.`}
              platforms={platforms}
              platformsLoading={platformsLoading}
              selectedPlatform={selectedPlatform}
              onPlatformChange={handlePlatformChange}
              disabledPlatforms={['apple_vision']}
              countriesList={countriesList}
              countryOptions={countryOptions}
              countriesLoading={countriesLoading}
              selectedCountry={country}
              selectedLanguage={language}
              onCountryChange={handleTopDiscountCountryChange}
              onLanguageChange={handleTopDiscountLanguageChange}
              appliedFilters={topDiscountAppliedFilters}
              onRemoveFilter={handleRemoveTopDiscountFilter}
              lastUpdated={lastUpdated}
              onOpenFilters={() => setTopDiscountFilterPanelOpen(true)}
              rankSort={rankSort}
              onToggleSort={toggleRankSort}
            />
            <FilterPanel
              open={topDiscountFilterPanelOpen}
              sections={filterSections.length > 0 ? filterSections : undefined}
              // Platform is already selectable via the Meta Quest/Steam tabs above -
              // don't duplicate it inside the filter panel.
              hidePlatformSection
              onClose={() => setTopDiscountFilterPanelOpen(false)}
              onApply={handleTopDiscountFilterApply}
              initialValues={topDiscountAppliedFilters}
            />
          </div>
        </div>

        <div className="trending-content">
          {loading ? (
            <Row className="g-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <Col key={index} xs={12} sm={6}>
                  <div className="trending-card"><TopAppCardSkeleton /></div>
                </Col>
              ))}
            </Row>
          ) : (
            apps && apps.length > 0 ? (
              <>
                <Row className="g-3">
                  {displayApps.map((app, idx) => (
                    <Col key={app?.item_id || idx} xs={12} sm={6}>
                      <div className="trending-card"><TopAppCardM gamedata={app} priceLayout="discounts" headingLevel="h2" platformIcon={platformIconUrl} platformName={platformLabel} /></div>
                    </Col>
                  ))}
                </Row>
                {!loading && (
                  <div className="text-center">
                    {!showAll && apps.length > 8 ? (
                      <Button
                        className="see-more-btn"
                        onClick={() => setShowAll(true)}
                      >
                        See More
                      </Button>
                    ) : showAll ? (
                      <Button
                        className="see-less-btn"
                        onClick={() => setShowAll(false)}
                      >
                        See Less
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: 28 }}>
                <div style={{ textAlign: 'center', color: '#A1ACB8', fontSize: 16 }}>
                  {Object.keys(topDiscountAppliedFilters || {}).length > 0 ? 'No results matched with filter.' : 'No data available for the selected country/language.'}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
    </React.Fragment>
  )
}
